import { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { USD, magnitude } from '../../lib/format';
import { C, EASE_SPRING, glass, ink, text } from '../../lib/theme';
import type { FunilRow } from '../../lib/types';
import { IconFlag, IconHourglass, IconShieldCheck, IconUndo } from '../icons';
import { ChartEmpty } from './shared';

/**
 * 4 fatias (pedido explícito, 2026-09-09): antes só "Excluído por Exceção"
 * misturava dois motivos diferentes de um evento não contar. Cada uma ganhou
 * ícone próprio (pedido explícito, 2026-09-10: "mais identidade") em vez de
 * só um pontinho colorido — Em Análise (ampulheta) e Perda (escudo, mesmo
 * ícone do KPI "Perda Confirmada" ao lado, pra reconhecer que é o mesmo
 * número) são as fatias "reais"; Anulado (seta de reverter) e VDI
 * (bandeira) são as duas formas de "não contar".
 *
 * Cores (pedido explícito, 2026-09-10: "remova essas cores cinzas... não
 * deixar Anulado similar a VDI") — nada de tom neutro pros dois, cada um
 * tem cor própria já estabelecida no projeto: **verde** (`C.green`) pro
 * Anulado, mesma cor que o `ReasonClassifier` já usa pra categoria "regra"
 * ("a regra resolve/zera o valor" — mesmo racional aqui: foi resolvido, é
 * um estado positivo/concluído); **azul** (`C.blueSoft`) pro VDI, cor já
 * usada no projeto pra "ainda precisa de atenção/não resolvido" (mesma
 * lógica do `Cobertura da Auditoria` ficar azul quando abaixo da meta).
 * Verde vs azul é o contraste mais forte disponível na paleta pra esses
 * dois não se confundirem visualmente.
 */
const SLICES = [
  { key: 'em_analise', label: 'Em Análise', color: C.amber, Icon: IconHourglass,
    blurb: 'Ainda não auditado — está esperando revisão na Fila.' },
  { key: 'perda', label: 'Perda', color: C.churn, Icon: IconShieldCheck,
    blurb: 'Auditado, sem correção e sem motivo de regra. É receita perdida de verdade — o número que dói.' },
  { key: 'anulado', label: 'Anulado', color: C.green, Icon: IconUndo,
    blurb: 'Alguém corrigiu manualmente pra US$ 0 na tela de Evidência ("Corrigir") — o valor bruto existia, mas não é perda real (ex: cobrança duplicada). RESOLVIDO por um humano.' },
  { key: 'vdi', label: 'VDI', color: C.blueSoft, Icon: IconFlag,
    blurb: 'Tem motivo de regra (ex: "Contrato anual já pago") mas NINGUÉM corrigiu ainda — valor pendurado, sem decisão confirmada. Diferença pro Anulado: aqui falta alguém revisar e resolver.' },
] as const;

/**
 * Gráfico 3 — Funil/rosca de auditoria (PRD §9.3): dá a leitura de "quanto do
 * trabalho já foi feito" de relance. Hover realça a fatia e escurece as demais
 * (hover-highlight com dim, §9-D) e o centro mostra %+valor — sem "explosão" 3D.
 *
 * Layout reorganizado (pedido explícito: "melhor destaque na zona de cima") —
 * a repartição por fatia (antes uma lista fina embaixo da rosca) virou uma
 * fileira de estatísticas compactas NO TOPO do card, com o número em
 * destaque; a rosca ganhou o resto da altura pra si, maior e melhor
 * centralizada, sem disputar espaço com uma lista.
 *
 * Reorganizado de novo (pedido explícito, 2026-09-10: "organize mais essa
 * seção") — os 4 chips agora vêm em 2 grupos visuais, não uma fileira única
 * homogênea: "Em Análise"+"Perda" (números reais) de um lado, "Anulado"+
 * "VDI" (o que não conta) do outro lado com rótulo próprio — a estrutura do
 * layout já entrega o significado, sem precisar ler os 4 rótulos pra
 * entender que dois são categorias diferentes dos outros dois.
 *
 * Cada chip tem um popover de hover (mesma animação scale-grow do `InfoDot`
 * de `primitives.tsx`, reaproveitada aqui) explicando o cálculo — pedido
 * explícito de ser didático sobre a diferença Anulado vs VDI bem onde o
 * usuário está olhando o número, não só num tooltip genérico de fórmula.
 *
 * `size="lg"`: variante pro card da fileira de KPIs da Visão Geral, que é a
 * única coluna flexível (`1fr`) da fileira — cresce pra preencher o espaço
 * que sobra, e os chips/rosca/texto central crescem proporcionalmente junto
 * em vez de deixar vão vazio ao redor de elementos do mesmo tamanho de
 * sempre. Default `'md'` preserva o tamanho original (usado no Scale Modal
 * expandido, que já tem espaço de sobra por outro motivo — altura fixa de
 * 440px).
 */
export function FunilChart({ rows, size = 'md' }: { rows: FunilRow[]; size?: 'md' | 'lg' }) {
  const [hover, setHover] = useState<number | null>(null);
  const lg = size === 'lg';

  const data = SLICES.map((s) => {
    const row = rows.find((r) => r.fatia === s.key);
    return { ...s, value: magnitude(row?.value), count: Number(row?.count ?? 0) };
  }).filter((d) => d.value > 0 || d.count > 0);

  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (data.length === 0 || total === 0) return <ChartEmpty />;

  const focused = hover !== null ? data[hover] : null;
  const reais = data.filter((d) => d.key === 'em_analise' || d.key === 'perda');
  const excluidos = data.filter((d) => d.key === 'anulado' || d.key === 'vdi');

  function Chip({ d }: { d: (typeof data)[number] }) {
    const i = data.indexOf(d);
    const isHover = hover === i;
    return (
      <div
        onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
        style={{
          position: 'relative', padding: lg ? '14px 16px' : '9px 11px', borderRadius: lg ? 14 : 12,
          cursor: 'default', minWidth: 0,
          background: `${d.color}14`, border: `1px solid ${isHover ? `${d.color}66` : `${d.color}28`}`,
          opacity: hover === null || isHover ? 1 : 0.5,
          transition: 'opacity 0.18s ease, border-color 0.18s ease',
        }}
      >
        {isHover && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 9px)', left: '50%', zIndex: 30,
            width: lg ? 232 : 200, padding: '11px 13px', borderRadius: 13,
            transform: 'translateX(-50%) scale(1)', transformOrigin: 'bottom center',
            animation: `koInfoPop 0.2s ${EASE_SPRING}`,
            fontSize: 11.5, fontWeight: 500, lineHeight: 1.45, color: ink(0.88),
            ...glass, pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: d.color, marginBottom: 4 }}>
              {d.label} · {USD(d.value)} · {d.count} evento{d.count === 1 ? '' : 's'}
            </div>
            {d.blurb}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: lg ? 9 : 6 }}>
          <span style={{
            width: lg ? 27 : 20, height: lg ? 27 : 20, borderRadius: 8, flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${d.color}22`, color: d.color,
            border: `1px solid ${d.color}38`, boxShadow: `0 0 12px -4px ${d.color}99`,
          }}><d.Icon size={lg ? 14 : 11} /></span>
          <span style={{
            fontSize: lg ? 12.5 : 10, fontWeight: 800, letterSpacing: '0.02em', color: text.title,
            lineHeight: 1.25, minWidth: 0,
          }}>{d.label}</span>
        </div>
        <div style={{
          fontSize: lg ? 24 : 16.5, fontWeight: 800, letterSpacing: '-0.02em', color: d.color,
          fontVariantNumeric: 'tabular-nums', marginTop: lg ? 8 : 4,
        }}>{USD(d.value)}</div>
        <div style={{ fontSize: lg ? 13 : 10.5, color: text.body, marginTop: lg ? 2 : 1 }}>{d.count} evento{d.count === 1 ? '' : 's'}</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: lg ? 16 : 10, marginBottom: lg ? 20 : 14, flex: 'none', alignItems: 'stretch' }}>
        {reais.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: `repeat(${reais.length}, minmax(0,1fr))`,
            gap: lg ? 16 : 10, flex: reais.length,
          }}>
            {reais.map((d) => <Chip key={d.key} d={d} />)}
          </div>
        )}
        {excluidos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: lg ? 6 : 4, flex: excluidos.length, minWidth: 0 }}>
            <span style={{
              fontSize: lg ? 10.5 : 8.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase',
              color: ink(0.36), paddingLeft: 2,
            }}>Não contam como perda</span>
            <div style={{
              display: 'grid', gridTemplateColumns: `repeat(${excluidos.length}, minmax(0,1fr))`,
              gap: lg ? 16 : 10, flex: 1,
            }}>
              {excluidos.map((d) => <Chip key={d.key} d={d} />)}
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="label" cx="50%" cy="50%"
              innerRadius={lg ? '68%' : '60%'} outerRadius={lg ? '96%' : '94%'} paddingAngle={2} stroke="none"
              onMouseEnter={(_, i) => setHover(i)}
              onMouseLeave={() => setHover(null)}
              isAnimationActive={false}
            >
              {data.map((d, i) => (
                <Cell
                  key={d.key} fill={d.color}
                  opacity={hover === null || hover === i ? 1 : 0.28}
                  style={{ transition: 'opacity 0.18s ease', cursor: 'pointer' }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', textAlign: 'center',
        }}>
          <div style={{
            fontSize: lg ? 27 : 22, fontWeight: 800, letterSpacing: '-0.02em',
            color: focused ? focused.color : C.fg, fontVariantNumeric: 'tabular-nums',
          }}>
            {focused
              ? `${((focused.value / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
              : USD(total)}
          </div>
          <div style={{ fontSize: lg ? 12.5 : 11, color: ink(0.44), marginTop: lg ? 5 : 3, maxWidth: lg ? 130 : 104 }}>
            {focused ? focused.label : 'perda total no filtro'}
          </div>
        </div>
      </div>
    </div>
  );
}
